import { useId } from 'react';

/**
 * SORIGIN wordmark — the OFFICIAL brand logo (`public/images/logo/logo.svg`):
 * the real letterforms (viewBox 798×200) with their exact per-path multi-stop
 * brand gradients (lime → teal → bottle-green). Full colour, fully legible on the
 * light cream sidebar header. Standalone inline SVG with instance-unique gradient
 * ids so multiple copies on one page never collide.
 *
 * `SoriginWordmark` — full "SORIGIN" wordmark (expanded sidebar / headers).
 * `SoriginMonogram` — leading "S" glyph (collapsed sidebar rail).
 */

interface GradientStop {
  offset?: number;
  color: string;
}
interface GradientDef {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  stops: GradientStop[];
}

/** Shared stop palettes (the official logo reuses these across paths). */
const TEAL_DOWN: GradientStop[] = [
  { offset: 0.05, color: '#52BF9D' },
  { offset: 0.22, color: '#48B492' },
  { offset: 0.51, color: '#2E9777' },
  { offset: 0.89, color: '#04684A' },
  { offset: 0.93, color: '#006346' },
];
const LIME_TO_GREEN: GradientStop[] = [
  { color: '#A6CE39' },
  { offset: 0.13, color: '#A0CA38' },
  { offset: 0.29, color: '#90BF38' },
  { offset: 0.47, color: '#77AE37' },
  { offset: 0.67, color: '#529635' },
  { offset: 0.87, color: '#257734' },
  { offset: 0.99, color: '#076333' },
];
const GREEN_TO_LIME: GradientStop[] = [
  { color: '#076333' },
  { offset: 0.13, color: '#257734' },
  { offset: 0.33, color: '#529635' },
  { offset: 0.53, color: '#77AE37' },
  { offset: 0.71, color: '#90BF38' },
  { offset: 0.87, color: '#A0CA38' },
  { offset: 1, color: '#A6CE39' },
];
const GREEN_TO_MINT: GradientStop[] = [
  { offset: 0.07, color: '#006346' },
  { offset: 0.11, color: '#04684A' },
  { offset: 0.49, color: '#2E9777' },
  { offset: 0.78, color: '#48B492' },
  { offset: 0.95, color: '#52BF9D' },
];

/** The 20 official gradients (paint0..paint19), in the same order as the paths. */
const GRADIENTS: GradientDef[] = [
  {
    x1: 516.12, y1: 126.41, x2: 575.39, y2: 126.41,
    stops: [
      { color: '#9DC43C' },
      { offset: 0.14, color: '#97C03B' },
      { offset: 0.32, color: '#87B63A' },
      { offset: 0.51, color: '#6EA439' },
      { offset: 0.73, color: '#498D37' },
      { offset: 0.95, color: '#1C6E35' },
      { offset: 0.99, color: '#126835' },
    ],
  },
  { x1: 550.79, y1: 132.46, x2: 550.79, y2: 185.74, stops: TEAL_DOWN },
  {
    x1: 490.41, y1: 70.74, x2: 587.4, y2: 81.43,
    stops: [
      { offset: 0.11, color: '#52BF9D' },
      { offset: 0.24, color: '#48B492' },
      { offset: 0.47, color: '#2E9777' },
      { offset: 0.76, color: '#04684A' },
      { offset: 0.79, color: '#006346' },
    ],
  },
  { x1: 419.24, y1: 179.32, x2: 419.24, y2: 52.33, stops: TEAL_DOWN },
  { x1: 403.16, y1: 62.07, x2: 435.33, y2: 62.07, stops: LIME_TO_GREEN },
  { x1: 614.49, y1: 179.32, x2: 614.49, y2: 52.33, stops: TEAL_DOWN },
  { x1: 598.4, y1: -475.72, x2: 630.57, y2: -475.72, stops: LIME_TO_GREEN },
  { x1: 668.21, y1: 179.32, x2: 668.21, y2: 54.11, stops: GREEN_TO_LIME },
  { x1: 731.56, y1: 151.22, x2: 666.97, y2: 38.62, stops: TEAL_DOWN },
  { x1: 753.39, y1: 46.66, x2: 753.39, y2: 186.34, stops: LIME_TO_GREEN },
  { x1: 173.47, y1: 180.51, x2: 173.47, y2: 37.5, stops: TEAL_DOWN },
  { x1: 171.55, y1: 162.96, x2: 238.53, y2: 46.93, stops: GREEN_TO_LIME },
  { x1: 100.18, y1: 159.21, x2: 36.6404, y2: 122.53, stops: GREEN_TO_LIME },
  { x1: 35.2495, y1: 48.8, x2: 64.2695, y2: 117.53, stops: GREEN_TO_MINT },
  { x1: 60.0596, y1: 71.71, x2: 100.94, y2: 35.61, stops: LIME_TO_GREEN },
  { x1: 501.76, y1: 179.29, x2: 501.76, y2: 37.94, stops: LIME_TO_GREEN },
  { x1: 360.38, y1: 184.69, x2: 322.07, y2: 117.9, stops: LIME_TO_GREEN },
  { x1: 310.43, y1: 123.928, x2: 401.863, y2: 49.8287, stops: TEAL_DOWN },
  { x1: 340.996, y1: 89.0892, x2: 307.186, y2: 36.2764, stops: LIME_TO_GREEN },
  { x1: 296.6, y1: 179.32, x2: 296.6, y2: 39.53, stops: GREEN_TO_MINT },
];

const SORIGIN_LOGO_PATHS: string[] = [
  'M575.39 154.35V114.56C575.39 103.34 570.52 98.48 559.3 98.48H528.82C520.35 98.48 516.12 103.14 516.12 112.24C516.12 121.34 520.35 126.21 528.82 126.21H545.12C545.12 126.21 575.39 122.18 575.39 154.36V154.35Z',
  'M545.12 126.2V149.27C545.12 149.27 547.19 178.57 526.19 179.29C526.78 179.3 527.37 179.32 527.97 179.32C540.67 179.32 554.21 176.78 562.47 173.18C572.84 168.73 575.38 163.23 575.38 154.34C575.38 122.16 545.11 126.19 545.11 126.19L545.12 126.2Z',
  'M491.36 108.21C491.36 79.85 505.96 66.3 527.34 66.3C534.96 66.3 542.16 67.99 548.29 71.38C552.73 73.92 555.7 76.04 559.3 76.04C566.5 76.04 573.48 67.36 573.48 60.59C573.48 55.3 569.04 50.22 561.84 46.2C552.1 40.7 539.83 37.94 526.92 37.94C481.14 37.94 491.36 108.21 491.36 108.21Z',
  'M419.25 179.32C430.04 179.32 435.33 175.09 435.33 166.62V52.33C435.33 52.33 403.16 60.41 403.16 84.5V166.62C403.16 175.09 408.45 179.32 419.25 179.32Z',
  'M435.33 52.34C435.33 43.87 430.04 39.64 419.25 39.64C408.46 39.64 403.16 43.87 403.16 52.34V84.51C403.16 60.42 435.33 52.34 435.33 52.34Z',
  'M614.49 179.32C625.28 179.32 630.57 175.09 630.57 166.62V52.33C630.57 52.33 598.4 60.41 598.4 84.5V166.62C598.4 175.09 603.69 179.32 614.49 179.32Z',
  'M630.58 52.34C630.58 43.87 625.29 39.64 614.5 39.64C603.71 39.64 598.41 43.87 598.41 52.34V84.51C598.41 60.42 630.58 52.34 630.58 52.34Z',
  'M653.6 56.78V166.63C653.6 175.1 658.47 179.33 668.2 179.33C677.93 179.33 682.8 175.1 682.8 166.63V80.49C682.8 80.49 661.34 44.15 653.59 56.79L653.6 56.78Z',
  'M745.88 137L699.1 53.19C694.23 44.51 687.25 39.64 674.34 39.64C660.79 39.64 653.6 45.14 653.6 56.78C661.35 44.14 682.81 80.48 682.81 80.48L731.7 165.35C748.19 183.15 745.88 136.99 745.88 136.99V137Z',
  'M760.49 39.64C750.75 39.64 745.89 43.87 745.89 52.34V137C745.89 137 748.2 183.16 731.71 165.36C737 174.67 743.14 179.33 755.2 179.33C768.53 179.33 775.09 173.62 775.09 162.19V52.34C775.09 43.87 770.22 39.64 760.49 39.64Z',
  'M224.28 121.23C220.46 140.64 207.94 151.39 190.6 151.39C169.65 151.39 155.68 135.73 155.68 108C155.68 108 153.52 36.68 190.6 36.68C149.33 36.68 122.66 63.98 122.66 108C122.66 152.02 149.33 179.33 190.6 179.33C213.67 179.33 221.64 141.85 224.28 121.23Z',
  'M190.6 36.68C153.52 36.68 155.68 108 155.68 108C155.68 80.28 169.65 64.61 190.6 64.61C211.55 64.61 225.52 80.27 225.52 108C225.52 112.78 225.08 117.18 224.28 121.23C221.64 141.85 213.67 179.33 190.6 179.33C231.87 179.33 258.54 152.03 258.54 108C258.54 63.97 231.87 36.68 190.6 36.68Z',
  'M64.7404 89.61C74.1504 101.6 82.0904 109.79 80.9804 138.9C80.9804 146.31 73.7804 151.39 63.2004 151.39C53.8904 151.39 47.9604 147.58 41.4004 143.77C37.3804 141.44 34.6304 139.96 31.6604 139.96C24.6804 139.96 17.9004 149.06 17.9004 156.47C17.9004 161.34 20.8604 165.15 26.3704 168.96C35.6804 175.52 49.0204 179.33 62.9904 179.33C91.5604 179.33 113.76 163.65 112.73 137.21C111.35 101.97 82.2304 98.81 64.7504 89.62L64.7404 89.61Z',
  'M69.8495 91.67C57.7495 86.78 50.7695 83.03 50.7095 76.25C50.7095 76.25 39.9095 40.51 61.8995 37.28C36.4795 39.14 18.4595 54.91 18.9695 77.94C19.9695 123.47 87.9495 109.59 79.0395 145C82.9495 139.09 85.4695 133.78 86.9695 129.01C91.6695 114.07 88.3695 99.14 69.8495 91.66V91.67Z',
  'M52.7296 81.88C52.7296 81.88 46.4396 65.72 67.0096 65.03C74.1996 64.79 80.1296 67.57 86.9096 70.96C91.5696 73.29 93.8896 74.35 96.2196 74.35C103.63 74.35 109.77 65.25 109.77 58.05C109.77 53.18 107.23 50.01 101.52 46.41C92.4196 40.69 80.1396 37.1 67.0196 37.1C65.2796 37.1 63.5796 37.16 61.9096 37.29C39.9196 40.52 40.0396 65.15 52.7396 81.89L52.7296 81.88Z',
  'M545.12 149.27C539.62 150.75 534.33 151.39 529.46 151.39C507.02 151.39 491.36 137.42 491.36 108.21C491.36 108.21 481.13 37.94 526.92 37.94C488.19 37.94 458.35 62.7 458.35 108.84C458.35 154.98 487.91 178.57 526.2 179.28C547.19 178.56 545.13 149.26 545.13 149.26L545.12 149.27Z',
  'M375.96 172.21C375.96 174.26 375.3 175.86 373.56 177.08C371.56 178.54 368.14 179.32 362.72 179.32C351.21 179.32 347.28 175.77 341.7 167.63L332.14 153.85C331.83 153.41 331.47 152.83 330.96 152.1C330.35 151.17 329.58 150.05 328.66 148.79C328.35 148.35 328 147.86 327.64 147.38C321.55 139.15 315.58 130.91 307.03 133.65C307.03 123.91 313.42 121.63 325.29 120.59C340.16 119.29 350.25 134.61 350.25 134.61L366.67 156.23C373.37 165.14 375.98 168.5 375.98 172.2L375.96 172.21Z',
  'M322.97 108.16C356.84 105.26 361.11 57.43 347.03 45.01C371.22 54.44 379.49 79.05 370.37 104.08C354.27 148.24 293.87 113.65 288.37 155.23C287.01 147.46 287.35 142.02 287.35 136.3C287.35 111.9 308.39 109.19 322.98 108.16H322.97Z',
  'M336.26 104.08C345.49 99.93 348.9 77.24 331.64 69.83C323.23 66.22 307.15 66.81 300.25 68.02C293.26 65.5 290.82 59.14 293.64 51.31C295.55 46.01 299.19 43.42 306 41.45C316.83 38.33 329.83 38.6 342.21 43.06C343.85 43.65 345.43 44.3 346.96 45C367.04 59.39 356.22 94.17 336.26 104.07V104.08Z',
  'M306.63 67.41C306.63 67.41 305.21 39.54 316.77 39.54C307.69 39.54 296.08 40.6 289.74 42.5C276.65 46.3 276.44 54.96 276.44 63.19V166.66C276.44 175.11 281.51 179.33 291.85 179.33C302.19 179.33 307.26 175.11 307.26 166.66V134.35L306.63 108.38V67.42V67.41Z',
];

/** Indices 11–13 are the leading "S" glyph (bbox ≈ x[17.9,112.7] y[37.1,179.3]). */
const S_GLYPH_INDICES = [11, 12, 13];

/** Renders the official brand letterforms, each filled with its exact gradient. */
function brandLogo(indices: number[], id: string) {
  return (
    <>
      <defs>
        {indices.map((i) => {
          const g = GRADIENTS[i];
          return (
            <linearGradient
              key={i}
              id={`${id}-g${i}`}
              x1={g.x1}
              y1={g.y1}
              x2={g.x2}
              y2={g.y2}
              gradientUnits="userSpaceOnUse"
            >
              {g.stops.map((s, j) => (
                <stop key={j} offset={s.offset !== undefined ? s.offset : 0} stopColor={s.color} />
              ))}
            </linearGradient>
          );
        })}
      </defs>
      {indices.map((i) => (
        <path key={i} d={SORIGIN_LOGO_PATHS[i]} fill={`url(#${id}-g${i})`} />
      ))}
    </>
  );
}

const ALL_INDICES = SORIGIN_LOGO_PATHS.map((_, i) => i);

export function SoriginWordmark({ className, title = 'Sorigin' }: { className?: string; title?: string }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 798 200" className={className} role="img" aria-label={title} preserveAspectRatio="xMidYMid meet">
      {brandLogo(ALL_INDICES, id)}
    </svg>
  );
}

export function SoriginMonogram({ className, title = 'Sorigin' }: { className?: string; title?: string }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="10 30 108 158" className={className} role="img" aria-label={title} preserveAspectRatio="xMidYMid meet">
      {brandLogo(S_GLYPH_INDICES, id)}
    </svg>
  );
}
